import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Send, Building2, Globe2, UserCheck, Sparkles, Users, Languages, Wallet, Store, ClipboardCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// ---------- OPTION LISTS (in Greek — the source content) ----------
const SALES_SKILLS = [
  "B2B Πωλήσεις", "International Sales", "Export Sales", "Business Development",
  "Lead Generation", "Market Research", "Market Scanning", "Cold Calling",
  "Cold Emailing", "LinkedIn Prospecting", "Κλείσιμο επαγγελματικών ραντεβού",
  "Παρουσιάσεις προϊόντων", "Διαπραγμάτευση", "Κλείσιμο εμπορικών συμφωνιών",
  "Ανάπτυξη δικτύου πωλήσεων", "Account Management",
  "Προσωπικές επισκέψεις σε υποψήφιους πελάτες", "Συμμετοχή σε επαγγελματικές εκθέσεις",
];
const CUSTOMER_TYPES = [
  "Χονδρέμποροι", "Εισαγωγείς", "Διανομείς", "Εμπορικοί αντιπρόσωποι",
  "Retailers", "Αλυσίδες καταστημάτων", "Supermarkets", "Φαρμακεία", "HoReCa",
  "Ξενοδοχεία", "Εστιατόρια", "Βιομηχανίες", "Κατασκευαστικές εταιρείες",
  "E-commerce επιχειρήσεις", "Δημόσιοι οργανισμοί", "Επαγγελματίες συγκεκριμένου κλάδου",
];
const LOCAL_KNOWLEDGE = [
  "Πολύ καλή γνώση της τοπικής αγοράς", "Προηγούμενη επαγγελματική δραστηριότητα στη χώρα",
  "Υφιστάμενο δίκτυο επαγγελματικών επαφών", "Επαφές με εισαγωγείς",
  "Επαφές με distributors", "Επαφές με wholesalers", "Επαφές με μεγάλες αλυσίδες",
  "Επαφές με decision makers", "Γνώση τοπικών εμπορικών πρακτικών",
];
const RESPONSIBILITIES = []; // deprecated — kept as empty for state compatibility
const OUTCOMES = [
  "Να διερευνήσουμε εάν υπάρχει ζήτηση", "Να εντοπίσουμε πιθανούς πελάτες",
  "Να δημιουργήσουμε βάση υποψήφιων πελατών", "Να επικοινωνήσουμε με υποψήφιους πελάτες",
  "Να κλείσουμε επαγγελματικά ραντεβού", "Να βρούμε εισαγωγέα", "Να βρούμε distributor",
  "Να βρούμε χονδρέμπορο", "Να βρούμε εμπορικό αντιπρόσωπο",
  "Να δημιουργήσουμε δίκτυο συνεργατών", "Να πραγματοποιήσουμε τις πρώτες πωλήσεις",
  "Να αναπτύξουμε συστηματικά τη συγκεκριμένη αγορά",
];
const CONTACT_POSITIONS = [
  "Ιδιοκτήτης", "CEO", "Purchasing Manager", "Sales Manager",
  "Import Manager", "Procurement Manager", "Commercial Director",
];
const EXPORT_READINESS = [
  "Αγγλικό website", "Εταιρική παρουσίαση στα Αγγλικά", "Κατάλογο προϊόντων",
  "Τιμοκατάλογο εξαγωγών", "Φωτογραφικό υλικό", "Videos προϊόντων",
  "Δείγματα προϊόντων", "Πιστοποιήσεις προϊόντων", "Τεχνικά φυλλάδια",
  "Όρους χονδρικής συνεργασίας", "MOQ / Ελάχιστη ποσότητα παραγγελίας",
  "Πολιτική αποστολής δειγμάτων",
];

// Services — 15 grouped categories
const SERVICE_GROUPS = [
  { key: "services_market_research", label: "Έρευνα & Σκανάρισμα Αγοράς", items: [
    "Έρευνα αγοράς συγκεκριμένης χώρας", "Ανάλυση ανταγωνισμού", "Εντοπισμός βασικών ανταγωνιστών",
    "Έρευνα τιμών ανταγωνισμού", "Έρευνα καναλιών διανομής", "Εντοπισμός σημαντικότερων εισαγωγέων",
    "Εντοπισμός σημαντικότερων distributors", "Εντοπισμός wholesalers", "Εντοπισμός retailers",
    "Εκτίμηση εμπορικού ενδιαφέροντος για το προϊόν", "Πρώτη αξιολόγηση καταλληλότητας αγοράς"] },
  { key: "services_client_database", label: "Δημιουργία Πελατολογίου", items: [
    "Δημιουργία στοχευμένης βάσης δυνητικών πελατών", "Συλλογή εταιρικών emails",
    "Συλλογή τηλεφώνων", "Εντοπισμός υπευθύνων αγορών", "Εντοπισμός decision makers",
    "Εντοπισμός LinkedIn profiles", "Κατηγοριοποίηση υποψήφιων πελατών", "Αξιολόγηση & προτεραιοποίηση leads"] },
  { key: "services_phone_sales", label: "Τηλεφωνικές Πωλήσεις", items: [
    "Cold Calling σε υποψήφιους πελάτες", "Παρουσίαση προϊόντων τηλεφωνικά",
    "Διερεύνηση ενδιαφέροντος", "Καταγραφή αναγκών", "Follow-up τηλεφωνικών επαφών",
    "Κλείσιμο εμπορικών ραντεβού", "Υπενθύμιση και επαναπροσέγγιση leads"] },
  { key: "services_email_outreach", label: "Email Marketing & Direct Outreach", items: [
    "Δημιουργία email παρουσίασης", "Templates ανά χώρα",
    "Προσωποποιημένη αποστολή emails", "Μαζική στοχευμένη αποστολή", "Follow-up campaigns",
    "Newsletter campaigns", "Παρακολούθηση απαντήσεων"] },
  { key: "services_linkedin", label: "LinkedIn", items: [
    "Αναζήτηση πελατών μέσω LinkedIn", "Αναζήτηση decision makers",
    "LinkedIn prospecting", "Αποστολή προσωποποιημένων μηνυμάτων",
    "Ανάπτυξη δικτύου", "LinkedIn Sales Navigator research"] },
  { key: "services_meetings", label: "Εμπορικές Συναντήσεις", items: [
    "Κλείσιμο online meetings", "Οργάνωση παρουσιάσεων", "Προετοιμασία παρουσίασης",
    "Συμμετοχή τοπικού συνεργάτη", "Μετάφραση / διερμηνεία", "Follow-up μετά τη συνάντηση"] },
  { key: "services_local_presence", label: "Προσωπική Παρουσία στην Αγορά", items: [
    "Προσωπικές επισκέψεις", "Παρουσίαση δειγμάτων", "Τοπικές συναντήσεις",
    "Εκπροσώπηση της εταιρείας", "Ανάπτυξη σχέσεων", "Τοπικός εκπρόσωπος"] },
  { key: "services_distributors", label: "Εισαγωγείς – Distributors – Αντιπρόσωποι", items: [
    "Αναζήτηση εισαγωγέα", "Αναζήτηση distributor", "Αναζήτηση αντιπροσώπου",
    "Αποκλειστικός συνεργάτης", "Αξιολόγηση υποψηφίων", "Παρουσίαση πρότασης",
    "Διαπραγμάτευση όρων", "Υποστήριξη μέχρι έναρξη"] },
  { key: "services_trade_shows", label: "Εμπορικές Εκθέσεις", items: [
    "Αναζήτηση εκθέσεων", "Εντοπισμός εκθετών & πελατών", "Κλείσιμο ραντεβού",
    "Τοπικός συνεργάτης για επισκέψεις", "Συλλογή leads", "Follow-up μετά την έκθεση"] },
  { key: "services_sales_materials", label: "Υλικό Πωλήσεων", items: [
    "Εταιρική παρουσίαση", "Sales presentation", "Κατάλογος προϊόντων",
    "Εμπορικά email", "Sales scripts", "Τηλεφωνικό script", "FAQ πωλητών",
    "Μετάφραση υλικού", "Προσαρμογή ανά χώρα"] },
  { key: "services_digital_presence", label: "Ψηφιακή Παρουσία", items: [
    "Αγγλικό website", "Landing page ανά αγορά", "Φόρμα ενδιαφέροντος B2B",
    "LinkedIn profile", "Ψηφιακός κατάλογος", "Online παρουσίαση προϊόντων"] },
  { key: "services_advertising", label: "Διαφημιστική Προώθηση", items: [
    "Google Ads ανά χώρα", "LinkedIn Ads", "Facebook / Instagram Ads",
    "B2B lead generation campaigns", "Remarketing", "Ads για distributors/partners"] },
  { key: "services_crm", label: "CRM & Παρακολούθηση Πωλήσεων", items: [
    "Καταχώριση leads σε CRM", "Παρακολούθηση τηλεφωνικών επαφών",
    "Παρακολούθηση emails", "Καταγραφή απαντήσεων", "Προγραμματισμός follow-ups",
    "Παρακολούθηση ραντεβού", "Αναφορές αποτελεσμάτων", "Κατηγοριοποίηση leads"] },
  { key: "services_reporting", label: "Reporting", items: [
    "Εβδομαδιαία αναφορά", "Μηνιαία αναφορά", "Αναφορά leads",
    "Αναφορά κλήσεων", "Αναφορά ενδιαφερομένων", "Αναφορά συναντήσεων",
    "Αναφορά feedback αγοράς", "Προτάσεις επόμενων βημάτων"] },
  { key: "services_continuous_dev", label: "Συνεχής Ανάπτυξη Αγοράς", items: [
    "Διατήρηση σταθερού συνεργάτη", "Συνεχής αναζήτηση πελατών",
    "Συνεχές follow-up", "Ανάπτυξη δικτύου", "Διαχείριση leads",
    "Επαναπροσέγγιση παλαιών leads", "Τοπική υποστήριξη πωλήσεων", "Μακροχρόνιο Business Development"] },
];

// ---------- HELPERS ----------
const toggle = (list, item) => list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

function CheckGrid({ options, values, onChange, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((opt) => {
        const checked = values.includes(opt);
        return (
          <label key={opt} className={`flex items-start gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm transition-colors ${checked ? "bg-[#0052CC]/5 border-[#0052CC] text-slate-900" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
            <input type="checkbox" checked={checked} onChange={() => onChange(toggle(values, opt))} className="mt-0.5 accent-[#0052CC]" data-testid={testidPrefix ? `${testidPrefix}-${opt.slice(0,15)}` : undefined} />
            <span className="leading-snug">{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

function Radios({ options, value, onChange, name }) {
  return (
    <div className="space-y-2">
      {options.map((opt) => (
        <label key={opt.v} className={`flex items-center gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors ${value === opt.v ? "bg-[#0052CC]/5 border-[#0052CC]" : "border-slate-200 hover:border-slate-300"}`}>
          <input type="radio" name={name} checked={value === opt.v} onChange={() => onChange(opt.v)} className="accent-[#0052CC]" />
          <span className="text-sm font-medium text-slate-800">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

// ---------- MAIN WIZARD ----------
const initialForm = {
  company_name: "", website: "", contact_person: "", contact_email: "", contact_phone: "",
  industry: "", products_to_promote: "", products_description: "",
  target_regions: "", priority_countries: "", has_existing_presence: false,
  existing_presence_countries: "", excluded_countries: "",
  partner_residence_requirement: "", industry_experience_requirement: "",
  required_industry: "", required_sub_industry: "", min_experience_years: "",
  sales_skills: [], sales_skills_other: "",
  customer_types: [], customer_types_other: "",
  languages: "", language_level: "", degree_requirement: "", preferred_field_of_study: "",
  local_knowledge: [],
  partner_responsibilities: [],
  desired_outcomes: [], desired_outcomes_other: "",
  ideal_customer_description: "", ideal_customer_size: "",
  ideal_contact_position: [], ideal_contact_position_other: "",
  export_readiness: [],
  wholesale_price: "", minimum_order: "", can_send_samples: null,
  exclusive_representation: "", private_label_option: null,
  start_horizon: "",
  budget_range: "", hourly_rate_offered: "", payment_model: "",
  services_market_research: [], services_client_database: [], services_phone_sales: [],
  services_email_outreach: [], services_linkedin: [], services_meetings: [],
  services_local_presence: [], services_distributors: [], services_trade_shows: [],
  services_sales_materials: [], services_digital_presence: [], services_advertising: [],
  services_crm: [], services_reporting: [], services_continuous_dev: [],
  services_most_interested: "", business_goal_description: "",
};

const STEPS = [
  { key: "business", label: "Επιχείρηση", icon: Building2 },
  { key: "markets", label: "Αγορές", icon: Globe2 },
  { key: "partner_profile", label: "Προφίλ Συνεργάτη", icon: UserCheck },
  { key: "skills", label: "Δεξιότητες", icon: Sparkles },
  { key: "customers", label: "Πελάτες", icon: Users },
  { key: "languages", label: "Γλώσσες & Σπουδές", icon: Languages },
  { key: "budget_pay", label: "Προϋπολογισμός & Αμοιβή", icon: Wallet },
  { key: "commercial", label: "Ιδανικός Πελάτης & Όροι", icon: Store },
  { key: "services", label: "Υπηρεσίες & Στόχος", icon: ClipboardCheck },
];

export default function DetailedQuestionnaire() {
  const { lang } = useI18n();
  const L = lang === "el"; // el default
  const [step, setStep] = useState(0);
  const [f, setF] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const canNext = () => {
    if (step === 0) return f.company_name && f.contact_person && f.contact_email && f.industry && f.products_to_promote && f.products_description;
    if (step === 1) return !!f.target_regions;
    if (step === 5) return !!f.languages;
    return true; // other steps are optional
  };

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/public/detailed-questionnaire", f);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(e?.response?.data?.detail || (L ? "Σφάλμα υποβολής" : "Submission failed"));
    } finally { setSubmitting(false); }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  if (done) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4" data-testid="questionnaire-done">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
          <Check className="w-8 h-8" />
        </div>
        <h2 className="mt-6 font-heading text-2xl font-bold text-slate-900">Ευχαριστούμε!</h2>
        <p className="mt-3 text-slate-600 text-sm leading-relaxed">
          Λάβαμε το ερωτηματολόγιό σας. Η ομάδα μας θα το μελετήσει και θα επικοινωνήσουμε μαζί σας
          εντός <b>48 ωρών</b>. Ένα email επιβεβαίωσης εστάλη στο <b>{f.contact_email}</b>.
        </p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-md font-semibold text-sm" data-testid="questionnaire-back-home">
          <ArrowLeft className="w-4 h-4" /> Επιστροφή στην αρχική
        </Link>
      </div>
    </div>
  );

  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900" data-testid="questionnaire-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="questionnaire-back">
            <ArrowLeft className="w-4 h-4" /> Πίσω
          </Link>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Βήμα {step + 1} από {STEPS.length}
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-[#0052CC] transition-all" style={{ width: `${progress}%` }} data-testid="questionnaire-progress" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {step === 0 && (
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0052CC]">Ερωτηματολόγιο αναγκών</div>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
              Διεθνής Ανάπτυξη & Προφίλ Συνεργάτη
            </h1>
            <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">
              Συμπληρώστε τα παρακάτω για να σχηματίσουμε μια πρώτη εικόνα των αναγκών σας. Παίρνει περίπου <b>10 λεπτά</b>. Αν κάποιο πεδίο δεν σας αφορά, μπορείτε να το προσπεράσετε.
            </p>
          </div>
        )}

        {/* Step title */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center">
            <StepIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Ενότητα {step + 1}</div>
            <div className="font-heading text-xl font-bold text-slate-900">{STEPS[step].label}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-8 space-y-6">
          {/* --- STEP 0: BUSINESS --- */}
          {step === 0 && (
            <>
              <Field label="Επωνυμία επιχείρησης *"><Input value={f.company_name} onChange={(v) => set("company_name", v)} tid="q-company-name" /></Field>
              <Field label="Ιστοσελίδα"><Input value={f.website} onChange={(v) => set("website", v)} placeholder="https://..." /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Υπεύθυνος επικοινωνίας *"><Input value={f.contact_person} onChange={(v) => set("contact_person", v)} tid="q-contact-person" /></Field>
                <Field label="Τηλέφωνο"><Input value={f.contact_phone} onChange={(v) => set("contact_phone", v)} /></Field>
              </div>
              <Field label="Email *"><Input type="email" value={f.contact_email} onChange={(v) => set("contact_email", v)} tid="q-email" /></Field>
              <Field label="Κλάδος δραστηριότητας *"><Input value={f.industry} onChange={(v) => set("industry", v)} tid="q-industry" /></Field>
              <Field label="Προϊόντα ή υπηρεσίες προς προώθηση στο εξωτερικό *"><Input value={f.products_to_promote} onChange={(v) => set("products_to_promote", v)} tid="q-products" /></Field>
              <Field label="Σύντομη περιγραφή προϊόντων / υπηρεσιών *"><Textarea value={f.products_description} onChange={(v) => set("products_description", v)} rows={4} tid="q-products-desc" /></Field>
            </>
          )}

          {/* --- STEP 1: MARKETS --- */}
          {step === 1 && (
            <>
              <Field label="Σε ποιες χώρες ή περιοχές επιθυμείτε να αναπτύξετε τις πωλήσεις σας; *"><Textarea value={f.target_regions} onChange={(v) => set("target_regions", v)} rows={3} tid="q-target-regions" /></Field>
              <Field label="Υπάρχουν χώρες προτεραιότητας;"><Input value={f.priority_countries} onChange={(v) => set("priority_countries", v)} /></Field>
              <Field label="Έχετε ήδη παρουσία ή πελάτες στο εξωτερικό;">
                <div className="flex gap-2">
                  <ChoiceBtn active={f.has_existing_presence === true} onClick={() => set("has_existing_presence", true)}>Ναι</ChoiceBtn>
                  <ChoiceBtn active={f.has_existing_presence === false} onClick={() => set("has_existing_presence", false)}>Όχι</ChoiceBtn>
                </div>
              </Field>
              {f.has_existing_presence && (
                <Field label="Σε ποιες χώρες;"><Input value={f.existing_presence_countries} onChange={(v) => set("existing_presence_countries", v)} /></Field>
              )}
              <Field label="Χώρες στις οποίες δεν επιθυμείτε να δραστηριοποιηθείτε"><Input value={f.excluded_countries} onChange={(v) => set("excluded_countries", v)} /></Field>
            </>
          )}

          {/* --- STEP 2: PARTNER PROFILE --- */}
          {step === 2 && (
            <>
              <Field label="Ο συνεργάτης πρέπει να κατοικεί στη χώρα-στόχο;">
                <Radios name="residence" value={f.partner_residence_requirement} onChange={(v) => set("partner_residence_requirement", v)}
                  options={[{ v: "required", label: "Απαραίτητο" }, { v: "preferred", label: "Επιθυμητό" }, { v: "not_required", label: "Δεν είναι απαραίτητο" }]} />
              </Field>
              <Field label="Επαγγελματική εμπειρία στον κλάδο σας;">
                <Radios name="exp" value={f.industry_experience_requirement} onChange={(v) => set("industry_experience_requirement", v)}
                  options={[{ v: "required", label: "Απαραίτητο" }, { v: "preferred", label: "Επιθυμητό" }, { v: "not_required", label: "Δεν είναι απαραίτητο" }]} />
              </Field>
              <Field label="Κλάδος εμπειρίας"><Input value={f.required_industry} onChange={(v) => set("required_industry", v)} /></Field>
              <Field label="Υποκλάδος / κατηγορία προϊόντων"><Input value={f.required_sub_industry} onChange={(v) => set("required_sub_industry", v)} /></Field>
              <Field label="Ελάχιστη επιθυμητή εμπειρία">
                <Radios name="minexp" value={f.min_experience_years} onChange={(v) => set("min_experience_years", v)}
                  options={[{ v: "1", label: "1+ έτος" }, { v: "3", label: "3+ έτη" }, { v: "5", label: "5+ έτη" }, { v: "10", label: "10+ έτη" }, { v: "none", label: "Δεν υπάρχει ελάχιστη απαίτηση" }]} />
              </Field>
            </>
          )}

          {/* --- STEP 3: SKILLS --- */}
          {step === 3 && (
            <>
              <Field label="Ποιες δεξιότητες πωλήσεων να διαθέτει ο συνεργάτης;">
                <CheckGrid options={SALES_SKILLS} values={f.sales_skills} onChange={(v) => set("sales_skills", v)} testidPrefix="q-skill" />
              </Field>
              <Field label="Άλλη δεξιότητα"><Input value={f.sales_skills_other} onChange={(v) => set("sales_skills_other", v)} /></Field>
            </>
          )}

          {/* --- STEP 4: CUSTOMERS --- */}
          {step === 4 && (
            <>
              <Field label="Τύπος πελατών που θέλετε να προσεγγίσετε">
                <CheckGrid options={CUSTOMER_TYPES} values={f.customer_types} onChange={(v) => set("customer_types", v)} testidPrefix="q-cust" />
              </Field>
              <Field label="Άλλος τύπος"><Input value={f.customer_types_other} onChange={(v) => set("customer_types_other", v)} /></Field>
            </>
          )}

          {/* --- STEP 5: LANGUAGES & EDUCATION --- */}
          {step === 5 && (
            <>
              <Field label="Ποιες γλώσσες πρέπει να γνωρίζει; *"><Input value={f.languages} onChange={(v) => set("languages", v)} placeholder="π.χ. Αγγλικά, Γερμανικά" tid="q-languages" /></Field>
              <Field label="Απαιτούμενο επίπεδο">
                <Radios name="lang_level" value={f.language_level} onChange={(v) => set("language_level", v)}
                  options={[{ v: "native", label: "Μητρική γλώσσα" }, { v: "excellent", label: "Άριστη γνώση" }, { v: "very_good", label: "Πολύ καλή γνώση" }, { v: "good", label: "Καλή γνώση" }]} />
              </Field>
              <Field label="Απαιτείται συγκεκριμένο πτυχίο;">
                <Radios name="degree" value={f.degree_requirement} onChange={(v) => set("degree_requirement", v)}
                  options={[{ v: "required", label: "Ναι" }, { v: "not_required", label: "Όχι" }, { v: "plus", label: "Θα θεωρηθεί επιπλέον προσόν" }]} />
              </Field>
              <Field label="Επιθυμητό αντικείμενο σπουδών"><Input value={f.preferred_field_of_study} onChange={(v) => set("preferred_field_of_study", v)} /></Field>
            </>
          )}

          {/* --- STEP 6: BUDGET & PAY --- */}
          {step === 6 && (
            <>
              <p className="text-sm text-slate-600 -mt-2">
                Ορίστε τον προϋπολογισμό σας και το ύψος αμοιβής που θα προσφέρετε στον συνεργάτη. Οι απαντήσεις μας βοηθούν να σας ταιριάξουμε με τους πιο κατάλληλους επαγγελματίες.
              </p>
              <Field label="Συνολικός μηνιαίος προϋπολογισμός">
                <Radios name="budget" value={f.budget_range} onChange={(v) => set("budget_range", v)}
                  options={[
                    { v: "under_500", label: "Έως €500 / μήνα" },
                    { v: "500_1000", label: "€500 – €1.000 / μήνα" },
                    { v: "1000_2000", label: "€1.000 – €2.000 / μήνα" },
                    { v: "2000_5000", label: "€2.000 – €5.000 / μήνα" },
                    { v: "5000_plus", label: "€5.000+ / μήνα" },
                    { v: "flexible", label: "Ευέλικτος — ανάλογα με την πρόταση" },
                  ]} />
              </Field>
              <Field label="Προσφερόμενη πληρωμή ανά ώρα εργασίας">
                <Radios name="hourly" value={f.hourly_rate_offered} onChange={(v) => set("hourly_rate_offered", v)}
                  options={[
                    { v: "8_10", label: "€8 – €10 / ώρα" },
                    { v: "10_15", label: "€10 – €15 / ώρα" },
                    { v: "15_20", label: "€15 – €20 / ώρα" },
                    { v: "20_30", label: "€20 – €30 / ώρα" },
                    { v: "30_plus", label: "€30+ / ώρα" },
                  ]} />
              </Field>
            </>
          )}

          {/* --- STEP 7: COMMERCIAL --- */}
          {step === 7 && (
            <>
              <Field label="Επιθυμητό αποτέλεσμα">
                <CheckGrid options={OUTCOMES} values={f.desired_outcomes} onChange={(v) => set("desired_outcomes", v)} />
              </Field>
              <Field label="Άλλο αποτέλεσμα"><Input value={f.desired_outcomes_other} onChange={(v) => set("desired_outcomes_other", v)} /></Field>
              <Field label="Περιγράψτε τον ιδανικό πελάτη σας"><Textarea value={f.ideal_customer_description} onChange={(v) => set("ideal_customer_description", v)} rows={3} /></Field>
              <Field label="Ιδανικό μέγεθος επιχείρησης">
                <Radios name="size" value={f.ideal_customer_size} onChange={(v) => set("ideal_customer_size", v)}
                  options={[{ v: "small", label: "Μικρή" }, { v: "medium", label: "Μεσαία" }, { v: "large", label: "Μεγάλη" }, { v: "any", label: "Δεν υπάρχει περιορισμός" }]} />
              </Field>
              <Field label="Θέση ανθρώπου με τον οποίο να επικοινωνήσουμε">
                <CheckGrid options={CONTACT_POSITIONS} values={f.ideal_contact_position} onChange={(v) => set("ideal_contact_position", v)} />
              </Field>
              <Field label="Υφιστάμενη εξαγωγική ετοιμότητα">
                <CheckGrid options={EXPORT_READINESS} values={f.export_readiness} onChange={(v) => set("export_readiness", v)} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Ενδεικτική τιμή χονδρικής"><Input value={f.wholesale_price} onChange={(v) => set("wholesale_price", v)} /></Field>
                <Field label="Ελάχιστη παραγγελία"><Input value={f.minimum_order} onChange={(v) => set("minimum_order", v)} /></Field>
              </div>
              <Field label="Δυνατότητα αποστολής samples;">
                <div className="flex gap-2">
                  <ChoiceBtn active={f.can_send_samples === true} onClick={() => set("can_send_samples", true)}>Ναι</ChoiceBtn>
                  <ChoiceBtn active={f.can_send_samples === false} onClick={() => set("can_send_samples", false)}>Όχι</ChoiceBtn>
                </div>
              </Field>
              <Field label="Δυνατότητα αποκλειστικής αντιπροσώπευσης ανά χώρα;">
                <Radios name="excl" value={f.exclusive_representation} onChange={(v) => set("exclusive_representation", v)}
                  options={[{ v: "yes", label: "Ναι" }, { v: "no", label: "Όχι" }, { v: "negotiable", label: "Κατόπιν συμφωνίας" }]} />
              </Field>
              <Field label="Δυνατότητα private label;">
                <div className="flex gap-2">
                  <ChoiceBtn active={f.private_label_option === true} onClick={() => set("private_label_option", true)}>Ναι</ChoiceBtn>
                  <ChoiceBtn active={f.private_label_option === false} onClick={() => set("private_label_option", false)}>Όχι</ChoiceBtn>
                </div>
              </Field>
              <Field label="Πότε επιθυμείτε να ξεκινήσει η προσπάθεια;">
                <Radios name="start" value={f.start_horizon} onChange={(v) => set("start_horizon", v)}
                  options={[{ v: "immediately", label: "Άμεσα" }, { v: "30d", label: "Εντός 30 ημερών" }, { v: "3m", label: "Εντός 3 μηνών" }, { v: "later", label: "Αργότερα" }]} />
              </Field>
            </>
          )}

          {/* --- STEP 8: SERVICES --- */}
          {step === 8 && (
            <>
              <p className="text-sm text-slate-600 -mt-2">
                Επιλέξτε όσες υπηρεσίες θεωρείτε ότι θα βοηθήσουν την επιχείρησή σας. Δεν είναι υποχρεωτικό να συμπληρωθούν όλες.
              </p>
              <div className="space-y-4">
                {SERVICE_GROUPS.map((group) => (
                  <details key={group.key} className="border border-slate-200 rounded-lg overflow-hidden group">
                    <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-semibold text-slate-800 hover:bg-slate-50 select-none">
                      <span>{group.label}</span>
                      <span className="text-xs text-slate-500 font-normal">
                        {f[group.key].length > 0 ? `${f[group.key].length} επιλεγμένα` : "Ανοίξτε για επιλογές"}
                      </span>
                    </summary>
                    <div className="px-4 py-3 border-t border-slate-100">
                      <CheckGrid options={group.items} values={f[group.key]} onChange={(v) => set(group.key, v)} />
                    </div>
                  </details>
                ))}
              </div>
              <Field label="Ποιες υπηρεσίες σας ενδιαφέρουν περισσότερο;"><Textarea value={f.services_most_interested} onChange={(v) => set("services_most_interested", v)} rows={3} /></Field>
              <Field label="Περιγράψτε με δικά σας λόγια τον στόχο σας">
                <Textarea value={f.business_goal_description} onChange={(v) => set("business_goal_description", v)} rows={5}
                  placeholder="Π.χ. Θέλουμε να βρούμε εισαγωγείς καλλυντικών στη Γερμανία και τη Γαλλία και να συνεργαστούμε με πωλητές που έχουν ήδη εμπειρία στον κλάδο..." tid="q-goal" />
              </Field>
            </>
          )}
        </div>

        {err && <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded" data-testid="questionnaire-error">{err}</div>}

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="questionnaire-prev"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-md text-sm font-semibold hover:bg-slate-50 disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4" /> Πίσω
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => canNext() && setStep(step + 1)}
              disabled={!canNext()}
              data-testid="questionnaire-next"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-md text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              Επόμενο <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="questionnaire-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold disabled:opacity-50 shadow-sm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Αποστολή..." : "Υποβολή ερωτηματολογίου"}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

// ---------- SMALL PRIMITIVES ----------
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-2">{label}</label>
      {children}
    </div>
  );
}
function Input({ value, onChange, placeholder, type = "text", tid }) {
  return <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={tid}
    className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/30 focus:border-[#0052CC]" />;
}
function Textarea({ value, onChange, rows = 3, placeholder, tid }) {
  return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={tid}
    className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0052CC]/30 focus:border-[#0052CC]" />;
}
function ChoiceBtn({ active, onClick, children }) {
  return <button type="button" onClick={onClick} className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${active ? "bg-[#0052CC] border-[#0052CC] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>{children}</button>;
}
